import type { Locator, ValidationIssue } from '../../core/issues.js';

import { toPointer } from '../../core/issues.js';

/**
 * Shared, pure answer-integrity checks for interactive activities.
 *
 * Each helper inspects a SINGLE activity's `setup` and returns issues; the
 * caller owns node iteration, activity-type filtering, and (for gameshow)
 * skipping reveal nodes. This keeps one implementation of each check that
 * story / lesson / dil / gameshow all delegate to, parameterised by `codePrefix`
 * and `setupPath` so each caller emits its own codes and envelope-rooted paths.
 */

const isStrictAlternatingSwipeDirections = (directions: string[]): boolean => {
  if (directions.length < 3) {
    return false;
  }
  for (let i = 1; i < directions.length; i++) {
    if (directions[i] === directions[i - 1]) {
      return false;
    }
  }
  return true;
};

/**
 * Break a strictly-alternating deck by swapping the last two cards. In an
 * alternating sequence the final two directions always differ, while the
 * third-from-last equals the last, so the swap creates an adjacent same-
 * direction pair — guaranteeing the result is no longer strictly alternating
 * while preserving every card and the left/right mix. Deterministic so the
 * fix is reproducible.
 */
const reorderToBreakAlternating = (cards: unknown[]): unknown[] => {
  const out = cards.slice();
  const last = out.length - 1;
  [out[last - 1], out[last]] = [out[last], out[last - 1]];
  return out;
};

export interface ActivityCheckArgs {
  /** The single activity's `setup` object under inspection. */
  setup: Record<string, unknown>;
  /** Path from the rule's input root to that `setup`, for issue paths. */
  setupPath: (string | number)[];
  /** Caller-owned issue-code prefix (e.g. `story.activity.swipeSelect`). */
  codePrefix: string;
  locator: Locator;
}

/**
 * swipe_select answer-integrity: cards must mix left/right and must not run in a
 * strict alternating pattern (the player could pass on rhythm alone). Re-derives
 * the `directions` array from `setup.cards`, counting only valid 'left'/'right'
 * values — identical to the shape rule's own derivation. Returns no issues when
 * `cards` is missing/empty (the shape rule reports that separately).
 */
export const swipeDirectionIssues = ({ setup, setupPath, codePrefix, locator }: ActivityCheckArgs): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];
  const cards = setup.cards;
  if (!Array.isArray(cards) || cards.length < 1) {
    return issues;
  }

  const cardsPath: (string | number)[] = [...setupPath, 'cards'];
  const directions: string[] = [];
  for (const card of cards) {
    if (!card || typeof card !== 'object') {
      continue;
    }
    const dir = (card as Record<string, unknown>).correctDirection;
    if (dir === 'left' || dir === 'right') {
      directions.push(dir);
    }
  }

  if (directions.length >= 2) {
    const hasLeft = directions.includes('left');
    const hasRight = directions.includes('right');
    if (!hasLeft || !hasRight) {
      issues.push({
        code: `${codePrefix}.singleDirection`,
        severity: 'error',
        message:
          'swipe_select cards must include both left and right correct answers (not all cards the same direction)',
        path: cardsPath,
        pointer: toPointer(cardsPath),
        locator,
        currentValue: directions,
        constraint: { kind: 'shape', detail: 'cards must mix left and right correctDirection values' },
      });
    }
  }

  if (isStrictAlternatingSwipeDirections(directions)) {
    // Only attach an auto-fix when every card carries a valid direction, so
    // the card order lines up with `directions` and a reorder is safe.
    const canAutoFix = directions.length === cards.length;
    issues.push({
      code: `${codePrefix}.strictAlternating`,
      severity: 'error',
      message:
        'swipe_select cards must not use a strict alternating pattern (e.g. right, left, right, left) — vary direction order so rhythm alone is not enough',
      path: cardsPath,
      pointer: toPointer(cardsPath),
      locator,
      currentValue: directions,
      constraint: { kind: 'custom', detail: 'avoid strict alternating direction pattern' },
      ...(canAutoFix
        ? {
            fix: {
              kind: 'replace' as const,
              value: reorderToBreakAlternating(cards),
              rationale: `Reordered swipe_select cards on node '${locator.nodeId}' to break the strict alternating direction pattern`,
            },
          }
        : {}),
    });
  }

  return issues;
};

/**
 * drag answer-integrity: an un-shuffled drag activity gives the answer away.
 *   - drag_ordering: options already in `correctAnswerOrder` and no shuffle.
 *   - drag_pairing: any pairing without shuffle (options render beside matches).
 * No-op for any other activity type or when `setup.shuffle === true`.
 */
export const dragPresolvedIssues = ({ activityType, setup, setupPath, codePrefix, locator }: ActivityCheckArgs & { activityType: string }): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];
  if (setup.shuffle === true) {
    return issues;
  }

  const nodeId = locator.nodeId ?? '(unknown)';

  if (activityType === 'drag_ordering') {
    const options = setup.options;
    const correctOrder = setup.correctAnswerOrder;
    if (!Array.isArray(options) || !Array.isArray(correctOrder)) {
      return issues;
    }
    const displayOrder = options.map(o =>
      o && typeof o === 'object' ? (o as Record<string, unknown>).value : undefined,
    );
    if (
      displayOrder.length === correctOrder.length
      && displayOrder.every((v, i) => v === correctOrder[i])
    ) {
      const path = [...setupPath, 'shuffle'];
      issues.push({
        code: `${codePrefix}.dragOrdering.presolved`,
        severity: 'error',
        message: `Node ${nodeId}: drag_ordering options are in the correct answer order with no shuffle - the activity is pre-solved. Set setup.shuffle to true (or author the options in a non-answer order).`,
        path,
        pointer: toPointer(path),
        locator,
        currentValue: setup.shuffle,
        constraint: { kind: 'shape', detail: 'DRAG_ORDERING_PRESOLVED' },
      });
    }
  }
  else if (activityType === 'drag_pairing') {
    const pairs = setup.pairs;
    if (!Array.isArray(pairs) || pairs.length < 1) {
      return issues;
    }
    const path = [...setupPath, 'shuffle'];
    issues.push({
      code: `${codePrefix}.dragPairing.presolved`,
      severity: 'error',
      message: `Node ${nodeId}: drag_pairing has no shuffle - each option renders beside the item it matches, so the activity is pre-solved. Set setup.shuffle to true.`,
      path,
      pointer: toPointer(path),
      locator,
      currentValue: setup.shuffle,
      constraint: { kind: 'shape', detail: 'DRAG_PAIRING_PRESOLVED' },
    });
  }

  return issues;
};
